/**
 * @description Componente de login para veterinários
 * @module RegularSignin
 * @since Outubro 2025
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  PersonOutlined as PersonIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from "@mui/icons-material";
import base64 from "base-64";
import logo from "../../assets/fluxmed-logo-horizontal.png";

import background from "../../assets/background.png";
import { useForm, Controller } from "react-hook-form";
import { useFormatter } from "@/utils/useFormatter";
import { useRegex } from "@/utils/useRegex";
import { useValidate } from "@/utils/useValidate";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { OAuthUser } from "@/services/auth";
import { MessageTypes, sendWebViewMessage } from "@/utils/webViewBridge";
import { CircularLoading } from "@/components/common/CircularLoading";
import { currentFeatures } from "@/configs/Settings";
import {
  navigateAfterSigninWithOptionalQuestionnaire,
  tryIngestQuestionnaireParamFromCurrentUrl,
} from "src-next/shared/patientQuestionnaireLink";
import {
  setRefreshToken,
  setUserAccessToken,
  setUserAccessTokenExpiresAt,
} from "src-next/shared/api/utils/tokenStorage";

// Interface para os dados do formulário de login
interface ISigninFormData {
  cpf: string;
  password: string;
}

const RegularSignin: React.FC = () => {
  const { signinCredentialType } = useValidate;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState(false);
  // Configuração do react-hook-form para login
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ISigninFormData>({
    mode: "onSubmit",
    defaultValues: {
      cpf: "",
      password: "",
    },
  });

  // Observar valores do formulário
  const watchedCpf = watch("cpf");
  const watchedPassword = watch("password");

  /**
   * Verifica se os dados do formulário são válidos
   */
  const isFormValid = () => {
    return (
      watchedCpf &&
      useValidate.cpfNumber(watchedCpf) &&
      watchedPassword &&
      watchedPassword.length >= 8
    );
  };
  function verificarBase64(baseCode: string) {
    try {
      window.atob(baseCode);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Extrai e decodifica parâmetros da URL (usado para login via app mobile)
  const url = window.location.href.split("params=")[1];
  const urlInformation = isMobile ? url?.split("&")[0] : url;
  const decodedParams = verificarBase64(urlInformation)
    ? base64.decode(urlInformation)
    : undefined;
  const identifier = decodedParams?.split(":")[0] || "";
  const password = decodedParams?.split(":")[1] || "";

  const tokenMobile = url?.split("&tokenMobile=")[1];

  /**
   * Lê `questionnarie` / `questionnaire` da URL (helper compartilhado com src-next),
   * persiste pending + flag de fluxo paciente e limpa a query da barra.
   */
  useEffect(() => {
    try {
      tryIngestQuestionnaireParamFromCurrentUrl();
    } catch {
      // falha silenciosa, não deve bloquear o login
    }
  }, []);

  const handlePostLoginRedirect = useCallback(() => {
    navigateAfterSigninWithOptionalQuestionnaire(navigate);
  }, [navigate]);

  /**
   * @description Realiza o processo de login no sistema
   * @returns {Promise<void>}
   * @throws {Error} Erro de autenticação
   */
  const loginApp = async () => {
    // Determina o tipo de credencial (email, telefone, usuário)
    const auxValue = signinCredentialType(identifier);
    const auxType =
      auxValue.includes("userName") || auxValue.includes("phoneNumbers.mobile");

    let credential;

    // Formata a credencial conforme o tipo
    if (auxType) {
      credential = identifier.replace(/[^a-zA-Z0-9]/g, "");
    } else {
      credential = identifier.replace("@", "[AT]");
    }

    setLoading(true);
    try {
      // Realiza autenticação via OAuth
      const response = await OAuthUser(credential, password);
      if (response) {
        setUserAccessToken(response.access_token);
        localStorage.setItem("logged", "true");
        localStorage.setItem("username", credential);
        setRefreshToken(response.refresh_token);
        localStorage.setItem("device_id", tokenMobile);
        setUserAccessTokenExpiresAt(Date.now() + response.expires_in * 1000);
        handlePostLoginRedirect();
      } else throw new Error("Sign in error");
    } catch (err) {
      // Notifica app mobile sobre falha no login
      // Notifica o app nativo sobre falha no login
      sendWebViewMessage({
        type: MessageTypes.LOGIN_FAILED,
      });
      toast.error("Erro, verifique suas credenciais!");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Processa o login
   */
  const onSubmit = async (data: ISigninFormData) => {
    const cleanCPF = data.cpf.replace(/\D/g, "");

    if (!useValidate.cpfNumber(cleanCPF)) {
      toast.error("CPF inválido!");
      return;
    }

    setLoading(true);
    try {
      const response = await OAuthUser(cleanCPF, data.password);
      if (response) {
        setUserAccessToken(response.access_token);
        localStorage.setItem("logged", "true");
        localStorage.setItem("username", cleanCPF);
        setRefreshToken(response.refresh_token);
        setUserAccessTokenExpiresAt(Date.now() + response.expires_in * 1000);
        handlePostLoginRedirect();
      } else throw new Error("Sign in error");
    } catch (err) {
      toast.error("Erro, verifique suas credenciais!");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Redireciona para a página de cadastro
   */
  const handleGoToSignup = () => {
    navigate("/signup");
  };

  /**
   * Redireciona para a página de recuperação de senha
   */
  const handleForgotPassword = () => {
    navigate("/recover");
  };

  /**
   * Manipula o pressionamento da tecla Enter
   */
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !loading && isFormValid()) {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    if (identifier && password) {
      loginApp();
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      {loading ? (
        <Stack
          width="100%"
          height="100vh"
          alignItems="center"
          justifyContent="center"
        >
          <CircularLoading />
        </Stack>
      ) : (
        <Container
          maxWidth="sm"
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pt: 15,
            pb: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Header com logo/título */}

          <Paper
            elevation={3}
            sx={{
              p: isMobile ? 2 : 4,
              borderRadius: "16px",
              width: "100%",
              backgroundColor: "#f0f0f0",
            }}
          >
            {/* Conteúdo do formulário */}
            <Box sx={{ py: 2 }} onKeyDown={handleKeyPress}>
              <Box
                sx={{
                  textAlign: "center",
                  mb: 3,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={logo}
                  width={217}
                  height="auto"
                  alt="Logo"
                  style={
                    currentFeatures.defaultApp
                      ? {
                          width: "250px",
                          height: "auto",
                        }
                      : {
                          height: "auto",
                          objectFit: "contain",
                          transform: "scale(2.5)",
                          margin: "0 auto 16px auto",
                        }
                  }
                />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                  Acesso à sua conta
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Informe seus dados para acessar o sistema
                </Typography>
              </Box>

              <Stack spacing={3}>
                {/* Campo CPF */}
                <Controller
                  name="cpf"
                  control={control}
                  rules={{
                    required: "CPF é obrigatório",
                    pattern: {
                      value: useRegex.cpf,
                      message: "CPF inválido",
                    },
                    validate: (value) =>
                      useValidate.cpfNumber(value) || "CPF inválido",
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      label="CPF"
                      value={value}
                      onChange={(e) =>
                        onChange(useFormatter.formatCPF(e.target.value))
                      }
                      error={!!errors.cpf}
                      helperText={errors.cpf?.message}
                      placeholder="000.000.000-00"
                      inputProps={{ maxLength: 14 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />

                {/* Campo Senha */}
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: "Senha é obrigatória",
                    minLength: {
                      value: 5,
                      message: "A senha deve ter no mínimo 8 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      label="Senha"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                {/* Botão de Login */}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={handleSubmit(onSubmit)}
                  disabled={loading}
                  sx={{
                    bgcolor: "#00A884",
                    py: 1.5,
                    "&:hover": {
                      bgcolor: "#008c6e",
                    },
                  }}
                >
                  {loading ? "Entrando..." : "Entrar"}
                </Button>

                {/* Links adicionais */}
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  sx={{ mt: 2 }}
                >
                  <Button
                    variant="text"
                    onClick={handleForgotPassword}
                    sx={{ color: "text.secondary" }}
                  >
                    Esqueci minha senha
                  </Button>

                  <Button
                    variant="text"
                    onClick={handleGoToSignup}
                    sx={{ color: "#00A884" }}
                  >
                    Criar uma conta
                  </Button>
                </Stack>

                {/* Links de primeiro acesso e informações */}
                <Stack spacing={1} alignItems="center" sx={{ mt: 3 }}>
                  {/* Primeiro acesso */}
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Primeiro acesso?
                    </Typography>
                    <Button
                      variant="text"
                      onClick={handleGoToSignup}
                      sx={{
                        color: "#00A884",
                        textTransform: "none",
                        p: 0,
                        minWidth: "auto",
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    >
                      Clique aqui
                    </Button>
                  </Stack>

                  {/* Saiba mais sobre o Fluxmed */}
                  <Button
                    variant="text"
                    href="https://fluxmed.com.br/"
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                    sx={{
                      color: "#00A884",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    Saiba mais sobre o Fluxmed
                  </Button>

                  {/* Política de privacidade */}
                  <Button
                    variant="text"
                    onClick={() => navigate("/privacy-policy")}
                    sx={{
                      color: "text.secondary",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    Política de privacidade
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Container>
      )}
    </Box>
  );
};

export default RegularSignin;
